// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import * as GuildBoostCommands from '@app/features/guild/commands/GuildBoostCommands';
import {showGuildErrorModal} from '@app/features/guild/components/alerts/GuildErrorModalUtils';
import {GuildBoostIntegratedProgress} from '@app/features/guild/components/GuildBoostIntegratedProgress';
import styles from '@app/features/guild/components/modals/guild_boost/GuildBoostConfirmModal.module.css';
import {
	GUILD_BOOST_APPLY_FAILED_DESCRIPTOR,
	GUILD_BOOST_CONFIRM_DESCRIPTION_DESCRIPTOR,
	GUILD_BOOST_CONFIRM_PRIMARY_DESCRIPTOR,
	GUILD_BOOST_CONFIRM_TITLE_DESCRIPTOR,
	GUILD_BOOST_LOADING_STATUS_DESCRIPTOR,
	GUILD_BOOST_NO_SLOTS_DESCRIPTOR,
	GUILD_BOOST_PREMIUM_REQUIRED_DESCRIPTOR,
	GUILD_BOOST_SLOTS_AVAILABLE_DESCRIPTOR,
	GUILD_BOOST_THANK_YOU_BODY_DESCRIPTOR,
	GUILD_BOOST_THANK_YOU_TITLE_DESCRIPTOR,
	GUILD_BOOST_YOUR_SLOTS_SECTION_DESCRIPTOR,
} from '@app/features/guild/utils/GuildBoostMessageDescriptors';
import {CLOSE_DESCRIPTOR, TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {resolveGuildBoostSidebarProgress} from '@app/features/guild/utils/guildBoostProgress';
import {VIEW_PLANS_DESCRIPTOR} from '@app/features/premium/utils/PremiumMessageDescriptors';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import {BoostGemBadgeIcon} from '@app/features/user/components/badges/LafonBadgeIcons';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {UserSettingsModal} from '@app/features/user/components/modals/UserSettingsModal';
import {GUILD_BOOST_SLOTS_PER_PREMIUM_USER} from '@fluxer/constants/src/GuildBoostConstants';
import type {GuildBoostStatusResponse} from '@fluxer/schema/src/domains/guild/GuildBoostSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useState} from 'react';

const BOOST_AGAIN_DESCRIPTOR = msg({
	message: 'Boost again',
	context: 'guild-boost',
	comment: 'Button in the boost dialog to apply another boost slot right after a successful boost.',
});

const PARTICLE_COLORS = ['#ff73fa', '#ffc4fd', '#d94bd3', '#ffffff', '#ff9cfb', '#c86bff'] as const;
const PARTICLE_COUNT = 14;

const logger = new Logger('GuildBoostConfirmModal');

interface GuildBoostConfirmModalProps {
	guildId: string;
	guildName: string;
	initialStatus?: GuildBoostStatusResponse;
	onApplied?: (status: GuildBoostStatusResponse) => void;
	/** Kept for callers; the dialog now celebrates inline and stays open while slots remain. */
	showThankYouOnSuccess?: boolean;
}

function BoostBurst({burstKey}: {burstKey: number}) {
	return (
		<div className={styles.particles} aria-hidden key={burstKey}>
			{Array.from({length: PARTICLE_COUNT}, (_, index) => {
				const angle = (index / PARTICLE_COUNT) * Math.PI * 2;
				const distance = 70 + (index % 3) * 22;
				return (
					<span
						key={index}
						className={styles.particle}
						style={
							{
								left: '50%',
								backgroundColor: PARTICLE_COLORS[index % PARTICLE_COLORS.length],
								animationDelay: `${(index % 4) * 40}ms`,
								'--dx': `${Math.cos(angle) * distance}px`,
								'--dy': `${Math.sin(angle) * distance * 0.7}px`,
							} as React.CSSProperties
						}
					/>
				);
			})}
		</div>
	);
}

/**
 * Boost dialog: shows level progress and the user's slots, applies a boost inline, celebrates in place and stays open
 * so every remaining slot can be used without reopening it.
 */
export const GuildBoostConfirmModal: React.FC<GuildBoostConfirmModalProps> = observer(
	({guildId, guildName, initialStatus, onApplied}) => {
		const {i18n} = useLingui();
		const [status, setStatus] = useState<GuildBoostStatusResponse | null>(initialStatus ?? null);
		const [isLoading, setIsLoading] = useState(initialStatus == null);
		const [isApplying, setIsApplying] = useState(false);
		const [celebrations, setCelebrations] = useState(0);

		useEffect(() => {
			if (initialStatus != null) return;
			let cancelled = false;
			(async () => {
				try {
					const next = await GuildBoostCommands.fetchGuildBoostStatus(guildId);
					if (!cancelled) setStatus(next);
				} catch (error) {
					logger.error('Failed to load boost status for confirm modal', error);
					if (!cancelled) {
						showGuildErrorModal({
							title: i18n._(GUILD_BOOST_APPLY_FAILED_DESCRIPTOR),
							message: i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR),
							dataFlx: 'guild.guild-boost-confirm-modal.load-error',
						});
						ModalCommands.pop();
					}
				} finally {
					if (!cancelled) setIsLoading(false);
				}
			})();
			return () => {
				cancelled = true;
			};
		}, [guildId, i18n, initialStatus]);

		const openPlutonium = useCallback(() => {
			ModalCommands.pop();
			ModalCommands.push(
				modal(() => (
					<UserSettingsModal initialTab="plutonium" data-flx="guild.guild-boost-confirm-modal.user-settings-modal" />
				)),
			);
		}, []);

		const handleApplyBoost = useCallback(async () => {
			if (isApplying) return;
			setIsApplying(true);
			try {
				const next = await GuildBoostCommands.applyGuildBoost(guildId);
				setStatus(next);
				setCelebrations((count) => count + 1);
				onApplied?.(next);
			} catch (error) {
				logger.error('Failed to apply guild boost', error);
				showGuildErrorModal({
					title: i18n._(GUILD_BOOST_APPLY_FAILED_DESCRIPTOR),
					message: i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR),
					dataFlx: 'guild.guild-boost-confirm-modal.apply-error',
				});
			} finally {
				setIsApplying(false);
			}
		}, [guildId, i18n, isApplying, onApplied]);

		const close = () => ModalCommands.pop();
		const me = status?.me;
		const boosted = celebrations > 0;
		const progress = status
			? resolveGuildBoostSidebarProgress({
					premiumTier: status.premium_tier,
					premiumSubscriptionCount: status.premium_subscription_count,
				})
			: null;

		return (
			<Modal.Root size="small" centered className={styles.root} data-flx="guild.guild-boost-confirm-modal.root">
				<Modal.ScreenReaderLabel text={i18n._(GUILD_BOOST_CONFIRM_TITLE_DESCRIPTOR)} />
				{boosted ? <BoostBurst burstKey={celebrations} /> : null}
				<div className={styles.hero}>
					<div className={styles.heroGlow} aria-hidden />
					<BoostGemBadgeIcon
						key={celebrations}
						size={64}
						className={clsx(styles.gem, boosted && styles.gemPop)}
					/>
					<h2 className={styles.title}>
						{boosted
							? i18n._(GUILD_BOOST_THANK_YOU_TITLE_DESCRIPTOR)
							: i18n._(GUILD_BOOST_CONFIRM_TITLE_DESCRIPTOR)}
					</h2>
					<p className={styles.subtitle}>
						{boosted
							? i18n._(GUILD_BOOST_THANK_YOU_BODY_DESCRIPTOR, {guildName})
							: i18n._(GUILD_BOOST_CONFIRM_DESCRIPTION_DESCRIPTOR)}
					</p>
				</div>
				{isLoading || !status || !me || !progress ? (
					<div className={styles.loading} aria-label={i18n._(GUILD_BOOST_LOADING_STATUS_DESCRIPTOR)}>
						<Spinner />
					</div>
				) : (
					<>
						<div className={styles.body}>
							<div className={styles.panel}>
								<GuildBoostIntegratedProgress
									level={progress.level}
									count={progress.count}
									nextRequired={progress.nextRequired}
									isMaxLevel={progress.isMaxLevel}
									ratio={progress.ratio}
									variant="relaxed"
								/>
							</div>
							{me.is_premium ? (
								<div className={styles.panel}>
									<div className={styles.slotsRow}>
										<span className={styles.slotsLabel}>{i18n._(GUILD_BOOST_YOUR_SLOTS_SECTION_DESCRIPTOR)}</span>
										<div className={styles.pips}>
											{Array.from({length: me.slots_total}, (_, index) => (
												<BoostGemBadgeIcon
													key={index}
													size={20}
													className={clsx(styles.pip, index >= me.slots_available && styles.pipUsed)}
												/>
											))}
										</div>
									</div>
									<span className={styles.slotsText}>
										{i18n._(GUILD_BOOST_SLOTS_AVAILABLE_DESCRIPTOR, {
											available: me.slots_available,
											total: me.slots_total,
											perPremium: GUILD_BOOST_SLOTS_PER_PREMIUM_USER,
										})}
									</span>
								</div>
							) : (
								<p className={styles.notice}>{i18n._(GUILD_BOOST_PREMIUM_REQUIRED_DESCRIPTOR)}</p>
							)}
							{me.is_premium && !me.can_boost && me.slots_available === 0 && !boosted ? (
								<p className={styles.notice}>{i18n._(GUILD_BOOST_NO_SLOTS_DESCRIPTOR)}</p>
							) : null}
						</div>
						<div className={styles.actions}>
							{!me.is_premium ? (
								<>
									<Button variant="secondary" onClick={close}>
										{i18n._(CLOSE_DESCRIPTOR)}
									</Button>
									<Button variant="primary" onClick={openPlutonium}>
										{i18n._(VIEW_PLANS_DESCRIPTOR)}
									</Button>
								</>
							) : me.can_boost ? (
								<>
									<Button variant="secondary" onClick={close}>
										{i18n._(CLOSE_DESCRIPTOR)}
									</Button>
									<Button
										variant="primary"
										onClick={handleApplyBoost}
										submitting={isApplying}
										data-flx="guild.guild-boost-confirm-modal.boost-button"
									>
										<BoostGemBadgeIcon size={16} className={styles.boostButtonIcon} />
										{boosted ? i18n._(BOOST_AGAIN_DESCRIPTOR) : i18n._(GUILD_BOOST_CONFIRM_PRIMARY_DESCRIPTOR)}
									</Button>
								</>
							) : (
								<Button variant="primary" onClick={close}>
									{i18n._(CLOSE_DESCRIPTOR)}
								</Button>
							)}
						</div>
					</>
				)}
			</Modal.Root>
		);
	},
);
